import { Database, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import StatusPill, { mapServerStatusToPill } from '../common/StatusPill';
import MetricsGrid from './MetricsGrid';
import { formatDate } from '../../fonctions/fonctions';

const ALERTE_COLOR = {
  4: '#dc2626',
  3: '#ea580c',
  2: '#d97706',
  1: '#16a34a',
};

/* ─── Affiche les noms des métriques en erreur ────────────── */
function MetricSummary({ metrics, isOffline, isCritical }) {
  if (!metrics || metrics.length === 0) {
    return <span style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>—</span>;
  }

  if (isOffline) {
    return (
      <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
        Dernières métriques connues
      </span>
    );
  }

  // Critique (alerte=4) : on montre uniquement les critiques
  // À surveiller (alerte 2-3) : on montre les métriques en anomalie
  const minAlerte = isCritical ? 4 : 2;
  const errorMetrics = metrics
    .filter(m => parseInt(m.alerte, 10) >= minAlerte)
    .sort((a, b) => parseInt(b.alerte, 10) - parseInt(a.alerte, 10));

  const nonCritCount = metrics.length - errorMetrics.length;

  if (errorMetrics.length === 0) {
    return (
      <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.78rem', fontWeight: 600, color: '#16a34a' }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#16a34a' }} />
        {metrics.length} métriques OK
      </span>
    );
  }

  const MAX_SHOWN = 4;
  const shown     = errorMetrics.slice(0, MAX_SHOWN);
  const remaining = errorMetrics.length - MAX_SHOWN;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
      {shown.map((m, i) => {
        const level = parseInt(m.alerte, 10);
        const color = ALERTE_COLOR[level] || ALERTE_COLOR[1];
        const name  = m.intitule || m.metric_name;
        return (
          <span
            key={i}
            style={{
              display:      'inline-flex',
              alignItems:   'center',
              gap:          4,
              padding:      '2px 8px',
              borderRadius: 99,
              fontSize:     '0.72rem',
              fontWeight:   600,
              background:   `${color}12`,
              color,
              border:       `1px solid ${color}30`,
              animation:    (level === 4 && isCritical) ? 'blink-pill 1.5s ease-in-out infinite' : 'none',
              whiteSpace:   'nowrap',
            }}
          >
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: color, flexShrink: 0 }} />
            {name}
          </span>
        );
      })}

      {remaining > 0 && (
        <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 500 }}>
          +{remaining} autre{remaining > 1 ? 's' : ''}
        </span>
      )}

      {nonCritCount > 0 && (
        <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
          · {nonCritCount} autres
        </span>
      )}
    </div>
  );
}

/* ─── Badge type du serveur ───────────────────────────────── */
function ServerTypeBadge({ typeId, type }) {
  const palette = {
    1: { bg: 'rgba(109,40,217,0.07)', color: '#6d28d9', border: 'rgba(109,40,217,0.18)' },
    2: { bg: 'rgba(37,99,235,0.07)',  color: '#1d4ed8', border: 'rgba(37,99,235,0.18)'  },
    3: { bg: 'rgba(5,150,105,0.07)', color: '#065f46', border: 'rgba(5,150,105,0.18)'  },
  };
  const p = palette[typeId] || palette[1];
  return (
    <span style={{
      display: 'inline-block', padding: '3px 11px', borderRadius: 99,
      fontSize: '0.74rem', fontWeight: 600,
      background: p.bg, color: p.color, border: `1px solid ${p.border}`,
    }}>
      {type}
    </span>
  );
}

/* ─── ServerRow ───────────────────────────────────────────── */
function ServerRow({ server, expanded, onToggle, onMetricClick, onOpenDrawer }) {
  const metrics    = server.metrics || [];
  const isOffline  = (server.server_status || '').toLowerCase().includes('hors ligne');
  const maxAlerte  = metrics.length ? Math.max(...metrics.map(m => parseInt(m.alerte, 10))) : 1;
  const isCritical = !isOffline && maxAlerte === 4;
  const isAnomalie = !isOffline && maxAlerte >= 2;
  const hasAlert   = isOffline || isCritical || isAnomalie;
  const pillStatus = isOffline   ? 'down'
                   : isCritical  ? 'critical'
                   : isAnomalie  ? 'watch'
                   : mapServerStatusToPill(server.server_status);

  const dotColor = isOffline || isCritical
    ? '#dc2626'
    : maxAlerte === 3 ? '#ea580c'
    : '#d97706';

  function openDrawer(e) {
    e.stopPropagation();
    onOpenDrawer(server);
  }

  function handleToggle(e) {
    e.stopPropagation();
    onToggle();
  }

  // Clignotement sur la bordure gauche si critique ou injoignable — pas de fond coloré
  const leftBorder = isOffline
    ? 'inset 3px 0 0 #94a3b8'
    : isCritical
      ? 'inset 3px 0 0 #dc2626'
      : 'none';

  const rowAnimation = isOffline ? 'blink-row 1.5s ease-in-out infinite' : 'none';

  return (
    <>
      <tr
        className="server-row"
        style={{
          cursor:    'pointer',
          boxShadow: leftBorder,
          animation: rowAnimation,
        }}
        onClick={onToggle}
      >
        {/* Nom */}
        <td className="sr-cell sr-cell-name">
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            {hasAlert && (
              <span style={{
                width: 8, height: 8, borderRadius: '50%',
                background: dotColor, flexShrink: 0,
                animation: (isOffline || isCritical) ? 'blink-pill 1.5s ease-in-out infinite' : 'none',
              }} />
            )}
            <span style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 28, height: 28, borderRadius: 8, flexShrink: 0,
              background: hasAlert ? `${dotColor}14` : 'var(--accent-dim)',
              color: hasAlert ? dotColor : 'var(--accent-light)',
            }}>
              <Database size={13} />
            </span>
            <span style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text)' }}>
              {server.serveur_name}
            </span>
          </div>
        </td>

        {/* Rôle */}
        <td className="sr-cell">
          <ServerTypeBadge typeId={server.server_type_id} type={server.server_type} />
        </td>

        {/* Statut */}
        <td className="sr-cell">
          <StatusPill
            status={pillStatus}
            label={
              isOffline             ? 'Injoignable' :
              isCritical            ? 'Opérationnel - Critique' :
              isAnomalie            ? 'Opérationnel - À surveiller' :
              server.server_status
            }
            pulse={isOffline}
          />
        </td>

        {/* Métriques */}
        <td className="sr-cell sr-cell-metrics">
          <MetricSummary metrics={metrics} isOffline={isOffline} isCritical={isCritical} />
        </td>

        {/* Date */}
        <td className="sr-cell sr-cell-date">
          {formatDate(server.serveur_last_update) || '—'}
        </td>

        {/* Actions */}
        <td className="sr-cell sr-cell-actions" onClick={e => e.stopPropagation()}>
          <div className="server-actions-wrap">
            <button className="sr-btn" onClick={openDrawer} title="Détails & graphiques">
              <ExternalLink size={13} />
            </button>
            <button className="sr-btn" onClick={handleToggle}>
              {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
          </div>
        </td>
      </tr>

      {expanded && (
        <tr>
          <td colSpan={6} style={{ padding: 0 }}>
            <MetricsGrid server={server} onMetricClick={onMetricClick} />
          </td>
        </tr>
      )}
    </>
  );
}

export default ServerRow;
